import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CompanyInformation from '../../components/companySettings/CompanyInformation';
import CompanySettings from '../../components/companySettings/CompanySettings';
import CompanyFinancialIntegration from '../../components/companySettings/CompanyFinancialIntegration';
import ClientOnboarding from '../../components/companySettings/ClientOnboardingImages';
import CompanyTaxes from '../../components/companySettings/CompanyTaxes';
import api from '../../api';
import ViewLoader from '../../components/shared/ViewLoader';
import { mainColors, secondaryColors } from '../../theme/themeConfig';

const NAV_ITEMS = [
  {
    group: 'Company',
    items: [
      { key: 'general', label: 'General' },
      { key: 'information', label: 'Information' },
      { key: 'integrations', label: 'Integrations' }
    ]
  },
  {
    group: 'Operations',
    items: [
      { key: 'onboarding', label: 'Client onboarding' },
      { key: 'calculator', label: 'Calculator' }
    ]
  }
];

const KEY_TO_INDEX: Record<string, number> = {
  general: 1,
  information: 0,
  integrations: 2,
  onboarding: 3,
  calculator: 4
};

function CompanySettingsPage() {
  const [activeKey, setActiveKey] = useState('general');
  const { state: params } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof params?.tab === 'number') {
      const entry = Object.entries(KEY_TO_INDEX).find(
        ([, v]) => v === params.tab
      );
      if (entry) setActiveKey(entry[0]);
    }
  }, [params]);

  const { data: currentCompany } = useSWR(
    'api/currentCompany',
    api.CompanyApi.getCurrentCompany
  );

  if (!currentCompany) {
    return <ViewLoader />;
  }

  const renderContent = () => {
    switch (activeKey) {
      case 'information':
        return <CompanyInformation companyInfo={currentCompany} />;
      case 'general':
        return <CompanySettings />;
      case 'integrations':
        return <CompanyFinancialIntegration />;
      case 'onboarding':
        return <ClientOnboarding />;
      case 'calculator':
        return <CompanyTaxes />;
      default:
        return <CompanySettings />;
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* Sidebar */}
      <Box
        sx={{
          background: '#F4F7FB',
          borderRight: `1px solid ${secondaryColors.SECONDARY_6}`,
          flexShrink: 0,
          minHeight: 'calc(100vh - 64px)',
          p: '24px 0',
          width: 220
        }}
      >
        <Typography
          sx={{
            color: '#0f1c2e',
            fontSize: '18px',
            fontWeight: 700,
            px: '20px',
            mb: 2
          }}
        >
          {t('COMPANY_SETTINGS.company')}
        </Typography>

        {NAV_ITEMS.map((group) => (
          <Box key={group.group} sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: secondaryColors.SECONDARY,
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '.8px',
                px: '20px',
                mb: 0.5,
                textTransform: 'uppercase'
              }}
            >
              {group.group}
            </Typography>
            {group.items.map((item) => {
              const isActive = activeKey === item.key;
              return (
                <Box
                  key={item.key}
                  onClick={() => setActiveKey(item.key)}
                  sx={{
                    alignItems: 'center',
                    background: isActive ? '#fff' : 'transparent',
                    borderLeft: isActive
                      ? `3px solid ${mainColors.MAIN}`
                      : '3px solid transparent',
                    color: isActive ? mainColors.MAIN : '#0f1c2e',
                    cursor: 'pointer',
                    display: 'flex',
                    fontSize: '13px',
                    fontWeight: isActive ? 600 : 400,
                    px: '20px',
                    py: '8px',
                    transition: 'all 0.15s',
                    '&:hover': {
                      background: isActive ? '#fff' : 'rgba(0,0,0,0.03)'
                    }
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
}

export default CompanySettingsPage;
